import React, { useMemo, useCallback } from 'react';
import DataGrid, { dataCyHeaderCell, useGridApiRef } from '@gogame/data-grid';
import { useScreenState } from '@gogame/screens';
import { Typography } from '@gogame/components';
import { isHourDimension } from 'scenes/Trackers/HourlyTracker/utils';

import NoDataPlaceholder from './NoDataPlaceholder';
import HourlyTrackerToolbar from './HourlyTrackerToolbar';
import { useNextLevel } from './useNextLevel';
import SmartTooltip from 'scenes/Trackers/components/SmartTooltip';
import {
  getComparePercentValue,
  getCompareValue,
  getCompareValueColor,
  getDifferenceValue,
  getPercentageDecorationModel,
  getPercentValue,
  getValue,
  getValueColor,
} from './utils';
import DimensionCell from './Columns/Dimension';
import DateHour from './Columns/DateHour';
import Hour from './Columns/Hour';

const TABLE_HEIGHT = 700;
const EMPTY_ARR = [];

const HourlyTrackerDataTable = ({
  rows = EMPTY_ARR,
  count = 0,
  drillItems = EMPTY_ARR,
  isLoading = true,
  isRefetching = false,
  onChangeSettings,
  onChangeDrillPath,
  onReorder,
  onCollapse,
  onTogglePercentage,
  onChangeColorScalesColumns,
  drillTo,
  dimension,
  dimensionName,
  columns = EMPTY_ARR,
  compareMode,
}) => {
  const apiRef = useGridApiRef();
  const screenState = useScreenState();

  const withDrillDown = useMemo(() => {
    return drillTo?.length > 0;
  }, [drillTo]);

  const pinnedRows = useMemo(() => {
    return rows.filter((row) => row.pinned);
  }, [rows]);

  const isHour = isHourDimension(dimension);

  const [nextLevel, setNextLevel] = useNextLevel();

  const onChangeNextLevel = useCallback(
    (value) => {
      setNextLevel(value);
    },
    [setNextLevel]
  );

  const decorationModel = React.useMemo(() => {
    return getPercentageDecorationModel(screenState.columns.percentage);
  }, [screenState.columns.percentage]);

  const visibleColumns = useMemo(() => {
    return [
      ...(isHour ? ['DateHour', 'Hour'] : ['Dimension']),
      ...screenState.columns.order,
    ];
  }, [screenState.columns.order, isHour]);

  return (
    <DataGrid
      rows={rows}
      count={count}
      apiRef={apiRef}
      loading={isLoading}
      fetching={isRefetching}
      ready={screenState.ready}
      columnsState={screenState.columns}
      hasChildrenOnServer={true}
      onChangeSettings={onChangeSettings}
      storageKey={'HourlyTracker'}
      tableSettings={screenState.tableSettings}
      onReorder={onReorder}
      onCollapse={onCollapse}
      withColorScale={true}
      onChangeColorScalesColumns={onChangeColorScalesColumns}
      decorationModel={decorationModel}
      onTogglePercentage={onTogglePercentage}
      pinnedRows={pinnedRows}
      minHeight={TABLE_HEIGHT}
      searchQuery={screenState.searchQuery}
      rowThreshold={0}
      notInitedLabel={<NoDataPlaceholder />}
      filtered={!!screenState.searchQuery}
      toolbar={
        <HourlyTrackerToolbar
          isDisabledExport={count > 50000}
          nextLevel={nextLevel}
          onChangeNextLevel={onChangeNextLevel}
          drillItems={drillItems}
          onChangeDrillPath={onChangeDrillPath}
        />
      }
      visibleColumns={visibleColumns}
    >
      <DataGrid.Column
        field={'Dimension'}
        fixed={true}
        resizable={false}
        unordered={true}
        disableReorder={true}
        sortable={true}
        width={300}
        withDrillDown={withDrillDown}
        nextLevel={nextLevel}
        compareMode={compareMode}
        dimension={dimension}
        dimensionName={dimensionName}
        formatter={(v, row, column, apiRef) => {
          const rowNode = apiRef.current?.getRowNode(row?.id);
          const { nextLevel } = column;
          if (rowNode.type === 'pinnedRow') {
            return 'Total';
          }
          return <DimensionCell value={v} nextLevel={nextLevel} />;
        }}
        renderHeader={() => (
          <Typography
            lineClamp={2}
            disableTooltip={true}
            data-cy={dataCyHeaderCell}
            variant={'caption'}
            sx={{
              whiteSpace: 'normal',
              wordBreak: 'break-all',
              fontWeight: 'inherit',
            }}
          >
            {dimensionName}
          </Typography>
        )}
      />

      <DataGrid.Column
        field={'DateHour'}
        fixed={true}
        resizable={false}
        disableReorder={true}
        sortable={true}
        width={150}
        withDrillDown={withDrillDown}
        nextLevel={nextLevel}
        compareMode={compareMode}
        dimension={dimension}
        dimensionName={dimensionName}
        getValue={(v) => [getValue(v), false]}
        getPercentValue={getPercentValue}
        getCompareValue={(v) => [getCompareValue(v), true]}
        getComparePercentValue={getComparePercentValue}
        getDifferenceValue={getDifferenceValue}
        type={'text'}
        placeholder={''}
        formatter={([v, compared], row, _, apiRef) => {
          const rowNode = apiRef.current?.getRowNode(row?.id);
          if (rowNode.type === 'pinnedRow') {
            return compared ? '' : 'Total';
          }
          return <DateHour value={v} dashed={compared} />;
        }}
        headerName={'Dates'}
      />

      <DataGrid.Column
        field={'Hour'}
        headerName={'Hour'}
        unordered={true}
        fixed={true}
        resizable={false}
        disableReorder={true}
        sortable={true}
        width={100}
        withDrillDown={withDrillDown}
        nextLevel={nextLevel}
        compareMode={compareMode}
        dimension={dimension}
        dimensionName={dimensionName}
        formatter={(v, row, column, apiRef) => {
          const rowNode = apiRef.current?.getRowNode(row?.id);

          if (rowNode.type === 'pinnedRow') {
            return 'Hours';
          }

          return (
            <Hour
              id={row?.Dimension.id}
              value={v?.value}
              nextLevel={column?.nextLevel}
            />
          );
        }}
      />

      {columns.map(
        ({
          name,
          type,
          displayName,
          tooltip,
          hint,
          fullDisplayName,
          displayFormula,
          showGlossary,
          glossaryItems,
          isHeuristics,
          isHighlightLower,
        }) => (
          <DataGrid.Column
            key={name}
            field={name}
            type={type}
            flex={true}
            minWidth={120}
            headerName={displayName}
            tooltip={tooltip}
            hint={hint}
            fullDisplayName={fullDisplayName}
            displayFormula={displayFormula}
            showGlossary={showGlossary}
            glossaryItems={glossaryItems}
            dotted={isHeuristics}
            isHighlightLower={isHighlightLower}
            withColorScale={['percent', 'currency', 'number'].includes(type)}
            withPercentage={['currency', 'number'].includes(type)}
            getValue={getValue}
            getPercentValue={getPercentValue}
            getCompareValue={getCompareValue}
            getComparePercentValue={getComparePercentValue}
            getDifferenceValue={getDifferenceValue}
            getValueColor={getValueColor}
            getCompareValueColor={getCompareValueColor}
            renderHeader={({ colDef }) => {
              const {
                headerName,
                fullDisplayName,
                displayFormula,
                hint,
                showGlossary,
                glossaryItems,
                align,
              } = colDef;

              return (
                <SmartTooltip
                  hoverDelay={1000}
                  anchor={
                    <Typography
                      lineClamp={2}
                      disableTooltip={true}
                      data-cy={dataCyHeaderCell}
                      variant={'caption'}
                      sx={{
                        whiteSpace: 'normal',
                        wordBreak: 'break-all',
                        fontWeight: 'inherit',
                      }}
                    >
                      {headerName}
                    </Typography>
                  }
                  name={fullDisplayName || headerName}
                  formula={displayFormula}
                  hint={hint}
                  showGlossary={showGlossary}
                  glossaryItems={glossaryItems}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: align,
                  }}
                  transformOrigin={{
                    vertical: 'bottom',
                    horizontal: align,
                  }}
                />
              );
            }}
          />
        )
      )}
    </DataGrid>
  );
};

export default HourlyTrackerDataTable;
